/**
 * Página MOBILE "Fluxo" (Task #31 Fase 2). READ-ONLY.
 *
 * Próximo 6DU (entradas/saídas/posição/saldo — mesma leitura do MobileResumo) +
 * saldo projetado dos próximos meses em LISTA simplificada (não a tabela densa do
 * desktop). Reusa getFluxo + apiRowsToMensal + projecaoSaldo6Meses (mesma fonte do
 * FinanceiroFluxoPage) e os hooks de 6du/posição. Sem entrada de dados.
 */
import { useEffect, useMemo, useState } from "react";
import { getFluxo } from "../../api/client";
import type { FluxoRow } from "../../api/types";
import { usePosicaoAberta } from "../../hooks/usePosicaoAberta";
import { useCalendarioCaixa } from "../../hooks/useCalendarioCaixa";
import { useProximo6du } from "../../hooks/useProximo6du";
import { useSaldoV2 } from "../../hooks/useSaldoV2";
import {
  CompromissadaFluxoLinha,
  composicaoAncoraFluxo,
} from "../financeiro/CompromissadaFluxoLinha";
import { formatarDataLongaBr, ymdIso } from "../../utils/dateBr";
import { fmtBRL } from "../../utils/formatters";
import {
  apiRowsToMensal,
  kpis6DuFromApi,
  projecaoSaldo6Meses,
} from "../../utils/mockData";
import { Card, ErroNote, MONEY, MobileHeader, Stat, sign } from "./mobileUi";

// Janela ampla: do mês passado até ~7 meses à frente (cobre 6du + projeção 6m).
function janelaFluxo(hoje = new Date()): { inicio: string; fim: string } {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return {
    inicio: iso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
    fim: iso(new Date(hoje.getFullYear(), hoje.getMonth() + 7, 0)),
  };
}

export function MobileFluxo({ onLogout, onRefresh }: {
  onLogout: () => void;
  onRefresh?: () => void;
  onNavigate?: (tab: import("./MobileShell").MobileTab) => void; // spread do MobileShell
}) {
  const [rows, setRows] = useState<FluxoRow[] | null>(null);
  // hardening A3: catch->null virava "—" mudo; erro agora e' estado proprio.
  const [erroRows, setErroRows] = useState(false);
  useEffect(() => {
    let cancel = false;
    const { inicio, fim } = janelaFluxo();
    getFluxo(inicio, fim) // #60: envelope {rows, as_of}
      .then((r) => { if (!cancel) { setRows(r.rows); setErroRows(false); } })
      .catch(() => { if (!cancel) { setRows(null); setErroRows(true); } });
    return () => { cancel = true; };
  }, []);

  const saldoV2 = useSaldoV2();
  const proximo6du = useProximo6du();
  const mesRef6du = proximo6du.info?.mes_referencia ?? null;
  const posAberta = usePosicaoAberta(mesRef6du);

  // review 03/07: projeção NUNCA ancora no mock — sem saldo real, sem lista.
  // 31/08: âncora COMPOSTA (disponível + compromissada ok) — paridade desktop.
  const compFluxo = composicaoAncoraFluxo(saldoV2.data);
  const saldoReal = compFluxo.total;
  const officialDate = proximo6du.info?.data ?? null;

  // 05/08 FONTE ÚNICA (paridade com o desktop): E/S do 6DU vêm do Calendário
  // de Caixa LIVE (linha eh_6du_atual do ciclo corrente) — o /api/fluxo legado
  // serve precompute 1×/dia e apodrecia intradia (card +3,05mi vs acumulado
  // +1,59mi em 05/08). Legado = fallback enquanto o calendário carrega.
  const calCaixa = useCalendarioCaixa();
  const kpis6du = useMemo(() => {
    if (officialDate) {
      const d = calCaixa.data.dias.find((x) => x.eh_6du_atual);
      if (d && d.data === ymdIso(officialDate)) {
        const entradas = Number(d.entra);
        const saidas = Math.abs(Number(d.sai));
        return { entradas, saidas, liquido: entradas - saidas };
      }
    }
    return rows && officialDate ? kpis6DuFromApi(rows, officialDate) : null;
  }, [rows, officialDate, calCaixa.data]);
  const temDados6du = kpis6du !== null;
  const posValor = Number(posAberta.data?.total_valor_mtm_estimado ?? "0");
  const posTemDados = !posAberta.isLoading && (posAberta.data?.linhas_posicao_aberta?.length ?? 0) > 0;
  const saldoTotal6du = (kpis6du ? kpis6du.entradas - kpis6du.saidas : 0) + (posTemDados ? posValor : 0);
  const temAlgum6du = temDados6du || posTemDados;

  // Próximos meses: projeção cumulativa (mesma fonte do desktop), só com dados reais.
  const projecao = useMemo(
    () =>
      rows && saldoReal !== null
        ? projecaoSaldo6Meses(saldoReal, apiRowsToMensal(rows, 6, new Date(), saldoReal))
        : [],
    [rows, saldoReal],
  );

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-fluxo">
      <MobileHeader subtitle="Fluxo" onLogout={onLogout} onRefresh={onRefresh} />
      <main className="px-4 py-4 flex flex-col gap-4 max-w-md mx-auto">
        <Card title="Próximo 6DU — Liquidação CCEE" testId="mobile-fluxo-6du">
          <div className="text-xl font-black text-white mb-1" data-testid="mobile-fluxo-6du-data">
            {officialDate ? formatarDataLongaBr(officialDate) : "—"}
          </div>
          {erroRows && <ErroNote testId="mobile-fluxo-erro" />}
          <Stat label="Entradas previstas" tone="pos" small
            value={temDados6du ? fmtBRL(kpis6du!.entradas, MONEY) : "—"} testId="mobile-fluxo-6du-entradas" />
          <Stat label="Saídas previstas" tone="neg" small
            value={temDados6du ? fmtBRL(kpis6du!.saidas, MONEY) : "—"} testId="mobile-fluxo-6du-saidas" />
          <Stat label="Posição aberta (est.)" tone={posValor >= 0 ? "pos" : "neg"} small
            value={posTemDados ? `${sign(posValor)}${fmtBRL(posValor, MONEY)}` : "—"} testId="mobile-fluxo-6du-posicao" />
          <div className="border-t border-white/10 mt-1 pt-1">
            <Stat label="Saldo total estimado" tone={saldoTotal6du >= 0 ? "turq" : "neg"}
              value={temAlgum6du ? `${sign(saldoTotal6du)}${fmtBRL(saldoTotal6du, MONEY)}` : "—"} testId="mobile-fluxo-6du-saldo" />
          </div>
        </Card>

        <Card title="Saldo projetado — próximos meses" testId="mobile-fluxo-projecao">
          <CompromissadaFluxoLinha comp={compFluxo} />
          {projecao.length === 0 ? (
            <div className="text-white/40 text-sm" data-testid="mobile-fluxo-projecao-vazio">—</div>
          ) : (
            <div className="flex flex-col divide-y divide-white/5">
              {projecao.map((m) => (
                <div key={m.mes} className="flex items-center justify-between gap-3 py-2" data-testid="mobile-fluxo-mes-row">
                  <span className="text-sm text-white/80 font-semibold w-16">{m.mes}</span>
                  <span className={`text-sm font-mono ${m.variacao >= 0 ? "text-indra-positivo" : "text-indra-negativo"}`}>
                    {sign(m.variacao)}{fmtBRL(m.variacao, MONEY)}
                  </span>
                  <span className="text-sm font-mono text-white text-right flex-1" data-testid="mobile-fluxo-mes-saldo">
                    {fmtBRL(m.saldoFinal, MONEY)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="text-white/40 text-[11px] mt-2">
            Variação (entradas − saídas) e saldo de caixa acumulado por mês. Detalhe
            completo (tabela + MtM) no desktop.
          </p>
        </Card>
      </main>
    </div>
  );
}
